import { cx } from '../../lib/cx';

/** Placeholder block. Size it with `className` (e.g. `h-(--mbm-space-5) w-1/2`). Never use a full-page spinner (KT §5.7). */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cx('animate-pulse rounded-(--mbm-radius-sm) bg-surface-tint', className)} />;
}

/** The first line gets a little shorter on each row, as in Figma, so a list doesn't look stamped. */
const LINE_WIDTHS = ['w-full', 'w-11/12', 'w-4/5'] as const;

/**
 * Figma P01 "Loading · skeleton": a rounded tile beside two lines, repeated. Match the real layout, so a list shows
 * this instead of a spinner while it loads. Announced once as "Loading", not once per bar.
 */
export function SkeletonRows({
  rows = 3,
  label = 'Loading',
  className,
}: {
  rows?: number;
  label?: string;
  className?: string;
}) {
  return (
    <div role="status" aria-label={label} className={cx('flex flex-col gap-(--mbm-space-3-5)', className)}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-(--mbm-space-3-5)">
          <Skeleton className="size-(--mbm-size-icon-tile-project) shrink-0 bg-surface-field" />
          <div className="flex w-2/5 min-w-0 flex-col gap-(--mbm-space-2)">
            <Skeleton
              className={cx('h-(--mbm-space-3) rounded-(--mbm-radius-xs) bg-surface-field', LINE_WIDTHS[i % 3])}
            />
            <Skeleton className="h-(--mbm-space-2-5) w-3/4 rounded-(--mbm-radius-xs)" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A whole page while its data loads: the heading's two lines, then a card of rows. */
export function PageSkeleton({ rows = 4, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-label={label} className="flex flex-col gap-(--mbm-space-6)">
      <div className="flex flex-col gap-(--mbm-space-2-5)">
        <Skeleton className="h-(--mbm-space-6) w-1/4 bg-surface-field" />
        <Skeleton className="h-(--mbm-space-3-5) w-2/5" />
      </div>
      <div className="rounded-(--mbm-component-radius-widget) bg-surface-card p-(--mbm-space-6) shadow-(--mbm-shadow-table-card)">
        <SkeletonRows rows={rows} label={label} />
      </div>
    </div>
  );
}
